import React, { useState } from 'react';
import { Globe, Home, Bookmark, History, Star, Trash2, Search, Plus, ArrowLeft, ArrowRight, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const BrowserPage = () => {
  const [bookmarks, setBookmarks] = useState([
    {
      id: 1,
      title: 'Google Drive',
      url: 'https://drive.google.com',
      category: 'Productivity',
      starred: true,
    },
    {
      id: 2,
      title: 'Project Management',
      url: 'https://asana.com',
      category: 'Work',
      starred: false,
    },
    {
      id: 3,
      title: 'Documentation',
      url: 'https://notion.so',
      category: 'Resources',
      starred: true,
    },
  ]);

  const [currentUrl, setCurrentUrl] = useState('https://google.com');
  const [urlInput, setUrlInput] = useState('https://google.com');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleNavigate = () => {
    setCurrentUrl(urlInput);
    toast.success('Navigating to ' + urlInput);
  };

  const handleBookmark = () => {
    toast.success('Page bookmarked!');
  };

  const handleStarToggle = (id) => {
    setBookmarks(bookmarks.map(bookmark => 
      bookmark.id === id ? { ...bookmark, starred: !bookmark.starred } : bookmark
    ));
  };

  return (
    <div className="relative h-screen flex flex-col bg-black overflow-hidden">
      {/* Hover trigger for sidebar */}
      <div 
        className="fixed top-0 right-0 w-4 h-full z-40"
        onMouseEnter={() => setSidebarOpen(true)}
      />

      {/* Auto-hiding Sidebar on Right */}
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-96 bg-black border-l-2 border-[#C9A961] transform transition-all duration-300 ease-in-out shadow-2xl overflow-hidden flex flex-col ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onMouseEnter={() => setSidebarOpen(true)}
        onMouseLeave={() => setSidebarOpen(false)}
      >
        {/* Search Bar at Top */}
        <div className="p-6 border-b border-[#C9A961]/30 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-[#C9A961]">Bookmarks</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className="text-[#C9A961] hover:bg-[#C9A961]/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
            <Input 
              placeholder="Search bookmarks..." 
              className="pl-10 bg-black/50 text-white border-0 focus:ring-2 focus:ring-[#C9A961]"
            />
          </div>
        </div>
        
        {/* Bookmarks List in Middle */}
        <div className="flex-1 overflow-y-auto">
          {bookmarks.map((bookmark) => (
            <div
              key={bookmark.id}
              onClick={() => { 
                setUrlInput(bookmark.url);
                setCurrentUrl(bookmark.url);
                setSidebarOpen(false);
                toast.success('Navigating to ' + bookmark.title);
              }}
              className="p-4 cursor-pointer border-b border-[#C9A961]/10 hover:bg-[#C9A961]/10 transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-1">
                  <Globe className="h-4 w-4 text-[#C9A961] flex-shrink-0" />
                  <p className="text-sm font-semibold text-white truncate">
                    {bookmark.title}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStarToggle(bookmark.id);
                  }}
                  className="text-[#C9A961] hover:bg-[#C9A961]/20 flex-shrink-0"
                >
                  <Star className={`h-3 w-3 ${bookmark.starred ? 'fill-[#C9A961]' : ''}`} />
                </Button>
              </div>
              <p className="text-xs text-gray-400 truncate">{bookmark.url}</p>
              <Badge className="mt-2 text-xs bg-[#C9A961]/20 text-[#C9A961] border border-[#C9A961]/50">
                {bookmark.category}
              </Badge>
            </div>
          ))}
        </div>

        {/* Navigation at Bottom */}
        <div className="p-4 border-t border-[#C9A961]/30 flex-shrink-0">
          <div className="space-y-1">
            <Button variant="ghost" className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm">
              <Home className="mr-2 h-4 w-4" />
              Home
            </Button>
            <Button variant="ghost" className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm">
              <Bookmark className="mr-2 h-4 w-4" />
              Bookmarks ({bookmarks.length})
            </Button>
            <Button variant="ghost" className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm">
              <Star className="mr-2 h-4 w-4" />
              Starred ({bookmarks.filter(b => b.starred).length})
            </Button>
            <Button variant="ghost" className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm">
              <History className="mr-2 h-4 w-4" />
              History
            </Button>
            <Button 
              variant="ghost" 
              onClick={handleBookmark}
              className="w-full justify-start text-[#C9A961] hover:bg-[#C9A961]/20 text-sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Bookmark
            </Button>
          </div>
        </div>
      </aside>

      {/* Header - Full Width */}
      <div className="p-8 border-b-2 border-[#C9A961] flex-shrink-0">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-6">
            <img src="/williams-logo-transparent.png" alt="Williams Diversified" className="h-24" />
            <div>
              <h1 className="text-4xl font-bold text-[#C9A961] mb-2">In-App Browser</h1>
              <p className="text-gray-400 text-lg">Browse any website - Link to projects & tasks</p>
            </div>
          </div>
        </div>
        
        {/* Browser Controls */}
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            <Button 
              variant="outline"
              size="icon"
              className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Button 
              variant="outline"
              size="icon"
              className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10"
            >
              <ArrowRight className="h-5 w-5" />
            </Button>
            <Button 
              variant="outline"
              size="icon"
              onClick={() => toast.success('Page refreshed')}
              className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10"
            >
              <RefreshCw className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Globe className="absolute left-4 top-4 h-5 w-5 text-gray-500" />
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleNavigate()}
                placeholder="Enter URL or search..."
                className="pl-12 bg-black/50 text-white text-lg py-6 border-0 focus:ring-2 focus:ring-[#C9A961]"
              />
            </div>
            <Button 
              onClick={handleNavigate}
              className="bg-[#C9A961] text-black hover:bg-[#D4C278] hover:scale-105 transition-all px-8"
            >
              Go
            </Button>
          </div>
          
          <Button 
            onClick={handleBookmark}
            variant="outline"
            className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10"
          >
            <Bookmark className="h-5 w-5 mr-2" />
            Bookmark
          </Button>
        </div>
      </div>

      {/* Browser Content - Full Width */}
      <div className="flex-1 overflow-hidden bg-black">
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <Globe className="h-32 w-32 text-[#C9A961]/30 mx-auto mb-8" />
            <h3 className="text-3xl font-bold text-gray-400 mb-4">Browser View</h3>
            <p className="text-gray-500 text-lg mb-2">Currently viewing: {currentUrl}</p>
            <p className="text-gray-600 text-sm">Full browser integration coming soon</p>
            <div className="mt-8 flex gap-4 justify-center">
              <Button 
                variant="outline"
                className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10"
              >
                Take Screenshot
              </Button>
              <Button 
                variant="outline"
                className="border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10"
              >
                Link to Project
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowserPage;